/**
 * B-GTCC-SVC-RULES-WIRE1 — typed read client for the Cockpit Services/Rules
 * tabs, matching the real server contract of
 * `services/api/src/modules/gtcc/svc/{types.ts,dto.ts}`.
 *
 * `./svc.ts` already reads the same two endpoints, but its `SvcServicesDto`
 * and `RulesSummaryDto` were transcribed from an earlier instruction body and
 * do not match what the server actually returns (see
 * `docs/design/gtcc-screens/B-GTCC-SVC-RULES-WIRE1/ABGLEICH.md`). This module
 * adds correctly-typed fetchers for the Cockpit tabs without touching
 * `svc.ts` or its existing `OvwView` consumer. The query keys are reused
 * as-is (same HTTP GET, same path) so React Query shares one cache entry
 * between `OvwView` and the Cockpit tabs.
 *
 * Read-only by construction: `gtccGet` wraps `apiGet` only.
 */
import {
  RULES_SUMMARY_KEY,
  RULES_SUMMARY_PATH,
  SVC_SERVICES_KEY,
  SVC_SERVICES_PATH,
} from "./svc.js";
import { gtccGet, type GtccMeasured, type GtccResult, type GtccStatus } from "./gtcc-result.js";

/* ---------------------------------------------------------------- types */

/** Per-service status enum of `services/api/.../svc/types.ts`. */
export const SERVICE_STATUSES = ["running", "ok", "degraded", "down", "unmeasurable"] as const;
export type ServiceStatus = (typeof SERVICE_STATUSES)[number];

export interface CockpitServiceRow {
  readonly key: string;
  readonly label: string;
  readonly status: ServiceStatus;
  /** `null` when the service exposes no version (only `postgres` is measured today). */
  readonly version: string | null;
  /** Always `null` today — the server has no restart-count source yet. */
  readonly restarts7d: number | null;
  readonly dependsOn: ReadonlyArray<string>;
  readonly reason?: string;
}

export interface CockpitDependencyNode {
  readonly key: string;
  readonly label: string;
}

export interface CockpitDependencyEdge {
  readonly from: string;
  readonly to: string;
  readonly kind: string;
}

export interface CockpitServicesDto extends GtccMeasured {
  readonly services: ReadonlyArray<CockpitServiceRow>;
  readonly dependencyGraph: {
    readonly nodes: ReadonlyArray<CockpitDependencyNode>;
    readonly edges: ReadonlyArray<CockpitDependencyEdge>;
  };
}

export interface CockpitStaleTarget {
  readonly name: string;
  readonly ageSeconds: number;
}

export interface CockpitRuleGroupSummary {
  readonly name: string;
  readonly rules: number;
  readonly firing: number;
  readonly verdict: "ok" | "warn" | "crit";
  readonly domain: string;
}

export interface CockpitRulesSummaryDto extends GtccMeasured {
  readonly targets: {
    readonly healthy: number;
    readonly down: number;
    readonly stale: ReadonlyArray<CockpitStaleTarget>;
  };
  readonly scrapeErrors24h: number;
  readonly groups: ReadonlyArray<CockpitRuleGroupSummary>;
}

/* -------------------------------------------------------------- fetchers */

export function fetchCockpitServices(
  signal?: AbortSignal,
): Promise<GtccResult<CockpitServicesDto>> {
  return gtccGet<CockpitServicesDto>(SVC_SERVICES_PATH, signal);
}

export function fetchCockpitRulesSummary(
  signal?: AbortSignal,
): Promise<GtccResult<CockpitRulesSummaryDto>> {
  return gtccGet<CockpitRulesSummaryDto>(RULES_SUMMARY_PATH, signal);
}

/* ----------------------------------------------------------- query keys */

/* Reused verbatim from ./svc.js: same path, same cache entry. */
export const COCKPIT_SVC_SERVICES_KEY = SVC_SERVICES_KEY;
export const COCKPIT_RULES_SUMMARY_KEY = RULES_SUMMARY_KEY;

export { SVC_SERVICES_PATH as COCKPIT_SVC_SERVICES_PATH };
export { RULES_SUMMARY_PATH as COCKPIT_RULES_SUMMARY_PATH };

export type { GtccResult, GtccStatus };
